import { useState, useEffect, useMemo, useRef } from 'react';
import { fetchStripeAccountId } from '@/services/transactions/stripeAccountService';
import { fetchSavingsCalculatorData, type SavingsCalculatorMetricsPayload } from '@/services/transactions/transactionFetchService';
import { useSubscriptionData, SubscriptionRecord } from '@/hooks/useSubscriptionData';
import { STRIPE_PERCENT_FEE, STRIPE_FIXED_FEE } from '@/utils/fees/constants';
import type {
  SavingsCalculatorData
} from '@/types/savingsCalculator';

export type { CardFeesByType, YearlyBreakdownRow, PaymentMethodBreakdown, AverageFeeMetrics, SavingsCalculatorData } from '@/types/savingsCalculator';

const SAVINGS_YEARS = 3; 
const createdSinceDefault = () =>
  Math.floor((Date.now() - SAVINGS_YEARS * 365.25 * 24 * 60 * 60 * 1000) / 1000);

let savingsModuleCache: {
  metrics: SavingsCalculatorMetricsPayload | null;
  hasFetched: boolean;
} = {
  metrics: null,
  hasFetched: false,
};


export const useSavingsCalculatorData = (stripeConnected: boolean): SavingsCalculatorData => {
  const [transactionMetrics, setTransactionMetrics] = useState<SavingsCalculatorMetricsPayload | null>(
    savingsModuleCache.metrics
  );
  const [transactionsLoading, setTransactionsLoading] = useState(!savingsModuleCache.hasFetched);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);

  const { subscriptions, loading: subscriptionsLoading } = useSubscriptionData({ stripeConnected });

  useEffect(() => {
    if (savingsModuleCache.hasFetched) return;
    if (!stripeConnected) {
      setTransactionsLoading(false);
      return;
    }
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    const load = async () => {
      setTransactionsLoading(true);
      setTransactionsError(null);
      try {
        const accountId = await fetchStripeAccountId();
        if (!accountId) {
          setTransactionsLoading(false);
          isFetchingRef.current = false;
          return;
        }
        const createdSince = createdSinceDefault();
        const metrics = await fetchSavingsCalculatorData(accountId, createdSince);
        setTransactionMetrics(metrics);
        savingsModuleCache = { metrics, hasFetched: true };
      } catch (err: unknown) {
        console.error('[SavingsCalculator] Error:', err);
        setTransactionsError(err instanceof Error ? err.message : 'Failed to fetch savings data');
      } finally {
        setTransactionsLoading(false);
        isFetchingRef.current = false;
      }
    };

    load();
  }, [stripeConnected]);

  const defaultMetrics: SavingsCalculatorMetricsPayload = {
    totalCardFeesPaid: 0,
    cardTransactionCount: 0,
    cardFeesByType: { oneTimeFees: 0, oneTimeCount: 0, subscriptionFees: 0, subscriptionCount: 0 },
    yearlyBreakdown: [],
    paymentMethodBreakdown: [],
    averageFeeMetrics: { avgFeePerTransaction: 0, avgFeePerCustomer: 0, uniqueCustomerCount: 0 },
  };
  const resolvedTransactionMetrics = transactionMetrics ?? defaultMetrics;

  const subscriptionMetrics = useMemo(() => {
    let feesOnActiveSubscriptions = 0;
    let activeCardSubscriptionCount = 0;

    subscriptions.forEach((sub: SubscriptionRecord) => {
      if (sub.status !== 'active') return;

      const amount = sub.amount || 0;
      if (amount <= 0) return;

      const stripeFee = Math.round(amount * STRIPE_PERCENT_FEE) + Math.round(STRIPE_FIXED_FEE * 100);

      let annualMultiplier = 1;
      switch (sub.billing_interval) {
        case 'month':
          annualMultiplier = 12;
          break;
        case 'week':
          annualMultiplier = 52;
          break;
        case 'day':
          annualMultiplier = 365;
          break;
        case 'year':
        default:
          annualMultiplier = 1;
          break;
      }

      feesOnActiveSubscriptions += stripeFee * annualMultiplier;
      activeCardSubscriptionCount++;
    });

    return {
      feesOnActiveSubscriptions: feesOnActiveSubscriptions / 100, 
      activeCardSubscriptionCount,
    };
  }, [subscriptions]);

  return {
    ...resolvedTransactionMetrics,
    ...subscriptionMetrics,
    loading: transactionsLoading || subscriptionsLoading,
    error: transactionsError,
  };
};
